import React from "react";
import { graphql, Link } from "gatsby";
import Layout from "../components/Layout";

import Img from "gatsby-image";
import FeatureGrid, { Feature } from "../components/Features";
import SubscribeBox from "../components/SubscribeForm";
import { HTMLContent } from "../components/Content";
import { Helmet } from "react-helmet";

import providerChart from "../img/charts/chart-return-to-stock.svg";

import {
  faTruck,
  faLock,
  faList,
  faSmile,
} from "@fortawesome/pro-regular-svg-icons";

const ProvidersPage = ({ data }) => {
  const { markdownRemark: page } = data;

  return (
    <Layout
      headerTitle={page.frontmatter.heading}
      headerPhoto={
        <Img
          imgStyle={{
            objectFit: "contain",
            objectPosition: "100% 100%",
          }}
          fluid={page.frontmatter.headerPhoto.childImageSharp.fluid}
        />
      }
      headerClassName="bg-accent curve-br"
      className="accent-foam"
    >
      <Helmet titleTemplate="%s">
        <title>{`${page.frontmatter.title}`}</title>
        <meta property="og:title" content={page.frontmatter.title} />
      </Helmet>

      <section className="container mt-8 pb-8 md:pb-12 lg:pb-16">
        <div className="columns">
          <div className="w-full lg:w-10/12">
            <h2 className="font-display font-bold text-3xl text-accent-m1 leading-tight">
              {page.frontmatter.subtitle}
            </h2>
          </div>
        </div>

        <div className="columns">
          <div className="w-full md:w-5/12 lg:w-4/12">
            <p className="text-xl leading-relaxed mb-3">
              {page.frontmatter.lead_paragraph}
            </p>

            <Link
              to={page.frontmatter.lead_cta_action}
              className="btn mt-3 btn-accent btn-avenir-semibold md:w-full"
            >
              {page.frontmatter.lead_cta}
            </Link>
          </div>
          <div className="w-0 hidden lg:block lg:w-1/12"></div>
          <div className="w-full md:w-7/12 lg:w-5/12 text-lg space-y-2">
            {
              // TODO: figure out how best to put this into the template for CMS
            }
            <p>
              We work with health systems and telemedicine companies to make it
              easy for patients to receive their prescriptions, adhere to
              therapy, and avoid readmission.
            </p>
            <p>
              Pairing telemedicine with delivery provides excellent care from
              diagnosis to daily dose.
            </p>
          </div>
        </div>
      </section>

      <div className="bg-accent-p2">
        <section className="py-6 pb-10 md:py-8 md:pb-20 lg:py-16 lg:pb-30">
          <div className="container">
            <div className="columns items-center">
              <div className="w-full md:w-5/12 lg:w-4/12 space-y-3">
                <h3 className="font-display font-bold text-3xl text-accent-m2 leading-tight">
                  Add Efficiency &amp; Improve Retention
                </h3>
                <p className="text-xl leading-relaxed">
                  Improve the efficiency of your business while keeping patients
                  on track with their medications.
                </p>
              </div>
              <div className="w-0 hidden lg:block lg:w-1/12"></div>
              <div className="w-full md:w-7/12 lg:w-5/12">
                <ul className="space-y-2 ml-5 lg:ml-0 list-disc">
                  <li>
                    Reduce reminder phone calls and manual delivery management;
                    give staff more time for patient consultation
                  </li>
                  <li>
                    Keep prescriptions in-house and reduce loss to other
                    pharmacies
                  </li>
                  <li>
                    Driver redundancies in place to ensure timely delivery of
                    medication
                  </li>
                  <li>
                    Dedicated customer success team to troubleshoot any issues
                  </li>
                  <li>
                    Consolidated invoicing to build administrative time back
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-navy sm:bg-accent-p2 sm:bg-curve-tr-d-sm-navy bg-stretch py-6 md:py-8 lg:py-16 addon-rollie-top-foam">
          <div className="container">
            <div className="columns items-center">
              <figure className="w-full md:w-6/12 text-center">
                <img
                  className="w-full sm:w-3/4 md:w-full mx-auto"
                  alt="Chart of prescriptions returned to stock"
                  src={providerChart}
                />
                <figcaption>
                  <p className="text-accent-p2">
                    Prescriptions Returned to Stock
                  </p>
                </figcaption>
              </figure>

              <div className="w-0 hidden lg:block lg:w-1/12"></div>
              <div className="w-full md:w-6/12 lg:w-5/12">
                <p className="text-white text-lg lg:text-xl leading-relaxed">
                  After implementing ScriptDrop’s delivery solution, a
                  metropolitan health center saw a dramatic drop in abandoned
                  prescriptions across all locations.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="py-6 md:py-8 lg:py-16">
        <div className="container">
          <div className="columns items-center">
            <div className="w-full lg:w-10/12">
              <FeatureGrid>
                <Feature
                  headingClassName="text-accent-m1"
                  heading="Keep the workflow simple"
                  icon={faTruck}
                >
                  <p className="mb-2">
                    Manage delivery within pharmacy software
                  </p>
                  <p className="mb-2">Real-time status updates</p>
                  <p className="mb-2">Co-pay collection or buy-down</p>
                  <p className="mb-2">High-touch customer service</p>
                </Feature>
                <Feature
                  headingClassName="text-accent-m1"
                  heading="Retain control"
                  icon={faLock}
                >
                  <p className="mb-2">Choose cost for patient</p>
                  <p className="mb-2">Tamper-evident packaging available</p>
                  <p className="mb-2">
                    Long-term storage of signatures and delivery details
                  </p>
                </Feature>
                <Feature
                  headingClassName="text-accent-m1"
                  heading="Choose from multiple service levels"
                  icon={faList}
                >
                  <p className="mb-2">Same-day Delivery</p>
                  <p className="mb-2">On-demand Delivery</p>
                  <p className="mb-2">Shipping</p>
                </Feature>
                <Feature
                  headingClassName="text-accent-m1"
                  heading="Improve the patient experience"
                  icon={faSmile}
                >
                  <p className="mb-2">
                    Secure delivery from pharmacy to patient’s doorstep
                  </p>
                  <p className="mb-2">
                    Access to a nationwide courier network with built-in
                    redundancies
                  </p>
                </Feature>
              </FeatureGrid>
            </div>
          </div>
        </div>
      </section>

      <section className="py-6 md:py-8 lg:py-16">
        <div className="container">
          <div className="columns items-center">
            <div className="w-full md:w-6/12 lg:w-5/12">
              <SubscribeBox title="Newsletter" />
            </div>

            <div className="hidden md:block w-1/12">&nbsp;</div>

            <div className="w-full text-center sm:text-left md:w-5/12 lg:w-4/12">
              <h4 className="font-display font-bold leading-tight text-2xl lg:text-3xl">
                Want to learn more?
              </h4>
              <div
                className="prose"
                dangerouslySetInnerHTML={{
                  __html: page.frontmatter.footer_cta_content,
                }}
              />
            </div>
          </div>
        </div>
      </section>

      <article className="container">
        <HTMLContent
          className="prose lg:prose-lg max-w-none"
          content={page.html}
        />
      </article>
    </Layout>
  );
};

export default ProvidersPage;

export const providerPageQuery = graphql`
  query ProvidersPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heading
        subtitle
        lead_paragraph
        lead_cta
        lead_cta_action
        footer_cta_content
        headerPhoto {
          childImageSharp {
            fluid(
              maxWidth: 256
              quality: 80
              traceSVG: { background: "transparent", color: "#339888" }
            ) {
              ...GatsbyImageSharpFluid_withWebp_tracedSVG
            }
          }
        }
      }
    }
  }
`;
